package org.matthewo.pages.event

import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.resources.*
import io.ktor.resources.serialization.*
import js.coroutines.awaitCancellation
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.matthewo.YEAR_PARAM
import org.matthewo.client
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.routes.API
import org.matthewo.useLoading
import react.*
import tanstack.react.router.useParams
import tanstack.react.router.useSearch
import tanstack.router.core.Params
import tanstack.router.core.Search

val RESOURCES_FORMAT = ResourcesFormat()

data class EventTabProps<T>(val coroutineScope: CoroutineScope, val year: Int?, val eventCode: String?, val data: T?)

inline fun <reified R : Any, reified T : Any> EventTabFC(
    crossinline eventDataResource: (event: API.Event) -> R?,
    crossinline block: @ReactDsl ChildrenBuilder.(EventTabProps<T>) -> Unit,
) = EventTabFC({ event, _ -> eventDataResource(event) }, block)

inline fun <reified R : Any, reified T : Any> EventTabFC(
    crossinline eventDataResource: (event: API.Event, pathParams: Params) -> R?,
    crossinline block: @ReactDsl ChildrenBuilder.(EventTabProps<T>) -> Unit,
) = EventTabFC({ event, params, _ -> eventDataResource(event, params) }, block)

inline fun <reified R : Any, reified T : Any> EventTabFC(
    crossinline eventDataResource: (event: API.Event, pathParams: Params, sarchParams: Search) -> R?,
    crossinline block: @ReactDsl ChildrenBuilder.(EventTabProps<T>) -> Unit,
) = FC("EventTab") {
    val coroutineScope = useCoroutineScope()

    val pathParams = useParams()
    val searchParams = useSearch()

    val year = pathParams[YEAR_PARAM]?.toIntOrNull()
    val eventCode = pathParams[EVENT_CODE_PARAM] ?: ""

    var data by useState<EventData<T>>()
    useEffect(year, eventCode) {
        data = null
    }

    useLoading(null == data)

    val resource = useMemo(pathParams, searchParams) {
        if (null == year) return@useMemo null
        eventDataResource(API.Event(year, eventCode), pathParams, searchParams)
    }
    if (null != resource && null != year) {
        useEffect(year, eventCode, href(RESOURCES_FORMAT, resource)) {
            val job = coroutineScope.launch {
                val load = suspend { refresh: Boolean ->
                    val response = client.get(resource) {
                        // the data just changed, so the service worker's cached copy is out of date
                        if (refresh) header(HttpHeaders.CacheControl, "no-cache")
                    }
                    data = EventData(year, eventCode, response.body<T>())
                }
                load(false)
                // a failed refresh keeps the current data and waits for the next change
                eventChanges(year, eventCode).collect { runCatching { load(true) } }
            }
            awaitCancellation { job.cancel() }
        }
    }

    if (year != data?.year) {
        block(EventTabProps(coroutineScope, null, null, null))
    } else {
        block(EventTabProps(coroutineScope, data?.year, data?.code, data?.data))
    }
}

data class EventData<T>(val year: Int, val code: String, val data: T)
